import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { StatsCard } from '../components/StatsCard';
import { StatusBadge } from '../components/StatusBadge';
import { Cloud, Mail, Users, HardDrive, Activity, ExternalLink } from 'lucide-react';

interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'degraded';
  responseTimeMs?: number;
}

interface MonitoringData {
  overall: string;
  services: ServiceStatus[];
  checkedAt: string;
}

interface CloudStats { users: number; files: number; storageMb: number }
interface MailStats { users: number; emailAccounts: number }

export function DashboardPage() {
  const [services, setServices] = useState<MonitoringData | null>(null);
  const [cloudStats, setCloudStats] = useState<CloudStats | null>(null);
  const [mailStats, setMailStats] = useState<MailStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [svc, cloud, mail] = await Promise.all([
          api.get<MonitoringData>('/monitoring/services'),
          api.get<CloudStats>('/admin/cloud/stats').catch(() => null),
          api.get<MailStats>('/admin/mail/stats').catch(() => null),
        ]);
        setServices(svc);
        setCloudStats(cloud);
        setMailStats(mail);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500">Laden ...</div>;
  }

  return (
    <div className="space-y-8 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">RLA Digital Solutions — Admin-Zentrale</p>
      </div>

      {/* Quick-Links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <a
          href="https://cloud.rla-solutions.de"
          target="_blank"
          rel="noopener noreferrer"
          className="card flex items-center gap-4 hover:border-rla-500 transition group"
        >
          <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
            <Cloud size={24} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 group-hover:text-rla-500 transition">RLA Cloud</div>
            <div className="text-sm text-slate-500">cloud.rla-solutions.de</div>
          </div>
          <ExternalLink size={16} className="text-slate-400" />
        </a>

        <a
          href="https://email.rla-solutions.de"
          target="_blank"
          rel="noopener noreferrer"
          className="card flex items-center gap-4 hover:border-rla-500 transition group"
        >
          <div className="p-3 rounded-lg bg-emerald-50 text-emerald-600">
            <Mail size={24} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 group-hover:text-rla-500 transition">RLA Mail</div>
            <div className="text-sm text-slate-500">email.rla-solutions.de</div>
          </div>
          <ExternalLink size={16} className="text-slate-400" />
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Cloud-User"
          value={cloudStats?.users ?? '—'}
          icon={<Users size={20} />}
          color="blue"
        />
        <StatsCard
          title="Dateien"
          value={cloudStats?.files ?? '—'}
          subtitle={cloudStats ? `${cloudStats.storageMb} MB` : undefined}
          icon={<HardDrive size={20} />}
          color="blue"
        />
        <StatsCard
          title="Mail-User"
          value={mailStats?.users ?? '—'}
          icon={<Users size={20} />}
          color="green"
        />
        <StatsCard
          title="E-Mail-Konten"
          value={mailStats?.emailAccounts ?? '—'}
          icon={<Mail size={20} />}
          color="green"
        />
      </div>

      {/* Service-Status */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={18} className="text-slate-500" />
          <h2 className="font-semibold text-slate-900">Service-Status</h2>
          {services && (
            <StatusBadge status={services.overall === 'healthy' ? 'online' : 'degraded'} />
          )}
        </div>
        <div className="space-y-3">
          {services?.services.map((s) => (
            <div key={s.name} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <span className="text-sm text-slate-700">{s.name}</span>
              <div className="flex items-center gap-3">
                {s.responseTimeMs !== undefined && (
                  <span className="text-xs text-slate-400">{s.responseTimeMs}ms</span>
                )}
                <StatusBadge status={s.status} />
              </div>
            </div>
          ))}
        </div>
        {services?.checkedAt && (
          <p className="text-xs text-slate-400 mt-3">
            Zuletzt geprüft: {new Date(services.checkedAt).toLocaleString('de-DE')}
          </p>
        )}
      </div>
    </div>
  );
}
